import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePapeis } from '../../../hooks/usePapeis';
import { alcanca } from '../../../shared/papeis';
import { PORTAL_COTACOES_HABILITADO } from '../../../api/portalCotacoes/portalCotacoesApi';
import { ORCAMENTO_CILIA_HABILITADO } from '../../../api/orcamentoCiliaApi';
import { ModuloNovo, papeisModuloNovo } from '../../../shared/modulos-novos';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import {
  ADMIN_SISTEMA_CILIA,
  ADMIN_SISTEMA_COMISSIONAMENTOS,
  ADMIN_SISTEMA_CONSUMO_IA,
  ADMIN_SISTEMA_LOGS,
  ADMIN_SISTEMA_CONTAC,
  ADMIN_SISTEMA_COTACOES,
  ADMIN_SISTEMA_GRUPOS,
  ADMIN_SISTEMA_INTEGRACOES,
  ADMIN_SISTEMA_OFICINA,
  ADMIN_SISTEMA_OFICINA_EXTERNA,
  ADMIN_SISTEMA_COMPRAS,
  ADMIN_SISTEMA_RADAR,
} from '../routes';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`) — é o que faz título, margens e responsividade baterem com as demais
// telas. O import é explícito de propósito: depender de CSS carregado por outra tela é
// acoplamento invisível, lição do BUG-V74 (at-67).
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';

/**
 * Hub da Administração do Sistema (at-73).
 *
 * A configuração do sistema deixou de ser uma barra única de abas e passou a ser
 * organizada POR MÓDULO. Hoje há três com configuração — Oficina, Cotações e Orçamento
 * Cilia (este entrou em 12/08/2026, pelo mesmo caminho do Portal: a tela existia dentro
 * do módulo, alcançável por um botão na lista de orçamentos) —, e a separação existe por
 * dois motivos concretos:
 *
 * 1. Escala: a barra de abas da Oficina já tem 5 abas e só cresce; três módulos
 *    novos estão a caminho (Orçamento Cilia, Portal de Cotações, Janela Única).
 * 2. Permissão: as abas da Oficina são ADMIN/TI, e a config de Cotações é de
 *    ADMIN/COMPRA. Como oitava aba, ou o COMPRA ganharia acesso ao resto, ou a aba
 *    apareceria quebrada para ele.
 *
 * ⚠️ Quem alcança um único módulo é REDIRECIONADO para ele — o hub não pode custar um
 * clique por dia a quem não tem escolha a fazer. Na prática, hoje:
 *
 * - **COMPRA** sempre cai direto em Cotações (não configura a Oficina nem o Cilia);
 * - **ADMIN e TI** caem direto na Oficina **em produção**, porque lá os dois módulos de
 *   Compras estão fora do ar (sem as envs) — o hub com mais de um cartão só aparece onde
 *   eles rodam;
 * - **TI alcança os três módulos** quando os dois backends estão no ar: o RoleGuard das
 *   telas do Portal é COMPRA/ADMIN/TI e o da config do Cilia é ADMIN/TI. Não presuma que
 *   o TI só configura a Oficina.
 */

/**
 * Os tópicos do hub (pedido do Victor, 25/09/2026: com 11 cartões numa grade só, a tela virou
 * uma lista sem ordem). A ORDEM deste array é a ordem na tela.
 *
 * ⚠️ Nenhum rótulo de tópico pode ser igual ao nome de um cartão ("Compras", "Oficina",
 * "Radar"…): os sentinelas de visibilidade procuram o cartão por `getByText`, que exige texto
 * exato — um cabeçalho homônimo faria a busca achar dois elementos e o teste quebrar.
 */
type Topico =
  | 'operacao'
  | 'compras'
  | 'financeiro'
  | 'inteligencia-artificial'
  | 'acessos-e-integracoes';

const TOPICOS: Array<{ chave: Topico; titulo: string }> = [
  { chave: 'operacao', titulo: 'Operação da oficina' },
  { chave: 'compras', titulo: 'Compras e peças' },
  { chave: 'financeiro', titulo: 'Financeiro' },
  { chave: 'inteligencia-artificial', titulo: 'Inteligência artificial' },
  { chave: 'acessos-e-integracoes', titulo: 'Acessos e integrações' },
];

interface ModuloConfig {
  chave: string;
  nome: string;
  descricao: string;
  rota: string;
  papeis: string[];
  topico: Topico;
  /**
   * Módulo com backend próprio só aparece quando a env dele existe — é o mecanismo que
   * mantém os dois módulos de Compras invisíveis em produção. `undefined` = sempre no ar
   * (a Oficina é o próprio backend da SPA).
   *
   * ⚠️ É FUNÇÃO, não booleano: lida no render, não na importação deste arquivo. Guardar o
   * valor aqui congelaria a flag no primeiro import e desligaria o controle por `jest.mock`
   * com getter que os sentinelas de visibilidade usam.
   */
  habilitado?: () => boolean;
  /**
   * Módulo NOVO (Portal de Cotações / Orçamento Cilia e suas superfícies): com a flag de
   * teste ligada (at-74), os papéis do cartão colapsam para ADMIN. Campo explícito em vez
   * de derivar de `habilitado` — o gate por env do cartão de Integrações está previsto
   * para sair na entrega 2, e a derivação pararia de cobrir o cartão em silêncio.
   */
  moduloNovo?: ModuloNovo;
}

const MODULOS: ModuloConfig[] = [
  {
    chave: 'oficina',
    topico: 'operacao',
    nome: 'Oficina',
    descricao:
      'Tags de esteira, fluxos de esteiras, checklist, pausas automáticas e especialidades de colaborador.',
    rota: ADMIN_SISTEMA_OFICINA(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    // at-114 (ADR 0059): a estrutura sobre a qual o inspetor trabalha. Sem gate por env — o
    // backend é o próprio SOAC.
    chave: 'oficina-externa',
    topico: 'operacao',
    nome: 'Oficina Externa',
    descricao:
      'Regiões, oficinas de terceiros, etapas informadas pela oficina, checklist da visita e o ' +
      'limiar de dias sem visita.',
    rota: ADMIN_SISTEMA_OFICINA_EXTERNA(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    chave: 'grupos-de-acesso',
    topico: 'acessos-e-integracoes',
    nome: 'Grupos de Acesso',
    descricao:
      'Conjuntos de perfis que você concede a várias pessoas de uma vez, com a tela inicial de cada grupo.',
    rota: ADMIN_SISTEMA_GRUPOS(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    // at-100: os de-paras que o Nasajon não entrega (nome do comprador, grafia da oficina, setor
    // do local de estoque). Sem gate por env — quem lê o Nasajon é o próprio backend do SOAC.
    chave: 'compras',
    topico: 'compras',
    nome: 'Compras',
    descricao:
      'Pausa e ritmo da sincronização com o Nasajon, unificação das oficinas e quais locais de ' +
      'estoque são de peças.',
    rota: ADMIN_SISTEMA_COMPRAS(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    chave: 'cotacoes',
    topico: 'compras',
    nome: 'Cotações',
    descricao:
      'Pesos dos presets de comparação, contatos de fornecedores e gestores de compras do Portal de Cotações.',
    rota: ADMIN_SISTEMA_COTACOES(),
    papeis: ['ADMIN', 'COMPRA', 'TI'],
    habilitado: () => PORTAL_COTACOES_HABILITADO,
    moduloNovo: 'portal-cotacoes',
  },
  {
    chave: 'cilia',
    topico: 'compras',
    nome: 'Requisições',
    descricao:
      'Padrões usados ao cadastrar peça no Nasajon, os vínculos de produto aprendidos na conferência e quem assina a requisição no ERP.',
    rota: ADMIN_SISTEMA_CILIA(),
    papeis: ['ADMIN', 'TI'],
    habilitado: () => ORCAMENTO_CILIA_HABILITADO,
    moduloNovo: 'orcamento-cilia',
  },
  {
    chave: 'integracoes',
    topico: 'acessos-e-integracoes',
    nome: 'Integrações',
    descricao: 'Envio de mensagens por plataforma externa, com a opção de voltar ao envio manual.',
    rota: ADMIN_SISTEMA_INTEGRACOES(),
    papeis: ['ADMIN', 'TI'],
    /*
     * ⚠️ Nesta entrega a única integração é o disparo de convites do Portal, cuja configuração
     * vive no backend dele — sem a env, a tela não teria a quem perguntar. Na entrega 2, quando
     * o checklist e a pesquisa do SOAC entrarem, o gate sai e o módulo passa a valer sempre.
     */
    habilitado: () => PORTAL_COTACOES_HABILITADO,
    moduloNovo: 'portal-cotacoes',
  },
  {
    // transversal/at-91 (ADR 0062): o log geral do sistema. Sem `habilitado` — o backend é o SOAC.
    chave: 'logs',
    topico: 'acessos-e-integracoes',
    nome: 'Logs do sistema',
    descricao:
      'Tudo o que foi feito no sistema: quem, quando, o quê e se deu certo, inclusive as tentativas recusadas.',
    rota: ADMIN_SISTEMA_LOGS(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    // at-12: consumo da API de IA. Sem `habilitado` — o backend é o próprio SOAC, e sem a
    // ANTHROPIC_API_KEY a tela ainda tem o que mostrar (zero consumo é resposta legítima).
    chave: 'consumo-ia',
    topico: 'inteligencia-artificial',
    nome: 'Consumo de IA',
    descricao:
      'Tokens e custo estimado das chamadas de inteligência artificial (assistente do painel e os radares).',
    rota: ADMIN_SISTEMA_CONSUMO_IA(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    // transversal/at-82 (ADR 0046). Sem `habilitado` — o backend é o próprio SOAC.
    //
    // 🔴 **at-83: o TI entrou aqui, e o motivo NÃO é que a restrição anterior estivesse errada.**
    // Até esta entrega o cartão era o único do hub com um papel só, porque o conteúdo (os achados)
    // carrega títulos e números do Contac, que o ADR 0012 mantém fora do alcance do TI — e isso
    // continua valendo. O que mudou foi a tela ganhar uma segunda aba, **Ativação**, que é
    // operação de sistema pura (ligar e desligar um radar), sem número nenhum. O TI alcança essa
    // aba e só ela: a de achados não é montada para ele, e as rotas dela continuam `@Roles(ADMIN)`
    // no backend.
    //
    // ⚠️ Reduzir este cartão de volta a `['ADMIN']` tira do TI a única forma de desligar um radar
    // que esteja consumindo o banco — que é o caso que originou a entrega (`BUG-007`).
    chave: 'radar',
    topico: 'inteligencia-artificial',
    nome: 'Radar',
    descricao:
      'Liga e desliga cada Radar, e mostra como os achados do assistente vêm sendo avaliados pela equipe.',
    rota: ADMIN_SISTEMA_RADAR(),
    papeis: ['ADMIN', 'TI'],
  },
  {
    // contac/at-13 (ADR 0007 do Contac). Sem `habilitado` — o backend é o próprio SOAC.
    //
    // ⚠️ Segundo cartão do hub SEM TI: o Contac é financeiro, e o ADR 0012 o mantém fora do
    // alcance dele (telas e rotas).
    //
    // 🔴 EMBARGO da contac/at-18: era `['ADMIN', 'CONTAC']` — o CONTAC entrava por ser o dono do
    // módulo, mesmo critério do cartão de Comissionamentos. Metade da tela descreve o Radar do
    // Relacionamento, que não pode ficar visível ao usuário final antes da apresentação à
    // diretoria; o CONTAC volta aqui quando o embargo cair (ver `PAPEIS_DO_RADAR_DO_RELACIONAMENTO`
    // no backend).
    chave: 'contac',
    topico: 'financeiro',
    nome: 'Contac',
    descricao:
      'A régua de cores do Dashboard de Placas e as regras do Radar do Relacionamento, com as medições que as sustentam.',
    rota: ADMIN_SISTEMA_CONTAC(),
    papeis: ['ADMIN'],
  },
  {
    // comissionamentos/at-01. Sem `habilitado`: o carimbo e o histórico vêm do backend do
    // SOAC, então a tela tem o que mostrar mesmo sem a env do painel de sincronização — só o
    // botão de disparo degrada (desabilitado com explicação).
    //
    // ⚠️ Primeiro cartão do hub SEM TI: o ADR 0012 mantém Comissionamentos fora do alcance
    // dele. E primeiro com papel operacional além de COMPRA — o COMISSIONAMENTO opera esta
    // sincronização (decisão do Victor, 03/09/2026).
    chave: 'comissionamentos',
    topico: 'financeiro',
    nome: 'Comissionamentos',
    descricao:
      'Sincronização dos boletos do SGA por data de vencimento — a base que o cálculo de repasse consome.',
    rota: ADMIN_SISTEMA_COMISSIONAMENTOS(),
    papeis: ['ADMIN', 'COMISSIONAMENTO'],
  },
];

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'Por que a configuração é separada por módulo',
    conteudo: (
      <p>
        Cada módulo do sistema tem as suas regras de configuração e os seus responsáveis. Juntar tudo numa
        tela só faria a lista crescer sem fim e obrigaria a dar acesso à configuração inteira para quem
        precisa de um pedaço dela. Aqui você escolhe o módulo e entra apenas na configuração dele.
      </p>
    ),
  },
  {
    titulo: 'Você pode ver menos módulos do que existem',
    conteudo: (
      <p>
        A lista mostra somente os módulos que o seu perfil pode configurar. Se houver apenas um, o sistema
        abre esse módulo direto, sem passar por esta tela. Módulo que não está no ar para a sua unidade
        também não aparece.
      </p>
    ),
  },
  {
    titulo: 'O que muda ao salvar uma configuração',
    conteudo: (
      <p>
        Configuração vale para o que acontece <strong>a partir dela</strong>. Nenhuma tela de configuração
        reprocessa o passado: horas já fechadas, cotações já enviadas e checklists já preenchidos continuam
        como estavam. Cada módulo explica o próprio detalhe na ajuda da sua tela.
      </p>
    ),
  },
];

const AdminSistemaHub: React.FC = () => {
  const navigate = useNavigate();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const papeisDoUsuario = usePapeis();

  const disponiveis = useMemo(
    () =>
      MODULOS.filter((modulo) => {
        if (modulo.habilitado && !modulo.habilitado()) return false;
        const papeis = modulo.moduloNovo
          ? papeisModuloNovo(modulo.papeis, modulo.moduloNovo)
          : modulo.papeis;
        // at-81: por INTERSEÇÃO — quem acumula funções vê os cartões dos dois papéis.
        return alcanca({ papeis: papeisDoUsuario }, papeis);
      }),
    [papeisDoUsuario],
  );

  // Um módulo só: o hub não pode custar um clique por dia a quem não escolhe nada.
  if (disponiveis.length === 1) return <Navigate to={disponiveis[0].rota} replace />;

  return (
    <div
      className="Frame1000004012"
      style={{ height: 'fit-content', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Administração do Sistema
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
            >
              ?
            </button>
          </div>
        </div>

        <p className="admin-hub-intro">Escolha o módulo que você quer configurar.</p>

        {disponiveis.length === 0 ? (
          <p className="erro-de-tela">
            Nenhum módulo de configuração está disponível para o seu perfil. Se você deveria ter acesso,
            acione a TI.
          </p>
        ) : (
          /* Tópico sem cartão disponível para este perfil NÃO é desenhado: um cabeçalho sobre
             nada seria lido como "há algo aqui que você não alcança". */
          TOPICOS.map((topico) => {
            const cartoes = disponiveis.filter((modulo) => modulo.topico === topico.chave);
            if (cartoes.length === 0) return null;
            return (
              <section key={topico.chave} className="admin-hub-topico" aria-label={topico.titulo}>
                <h2 className="admin-hub-topico-titulo">{topico.titulo}</h2>
                <div className="admin-hub-grid">
                  {cartoes.map((modulo) => (
                    <button
                      key={modulo.chave}
                      type="button"
                      className="admin-hub-card"
                      onClick={() => navigate(modulo.rota)}
                    >
                      <span className="admin-hub-card-nome">{modulo.nome}</span>
                      <span className="admin-hub-card-descricao">{modulo.descricao}</span>
                    </button>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Administração do Sistema"
        secoes={helpSections}
      />
    </div>
  );
};

export default AdminSistemaHub;
